import React, { useEffect, useState } from 'react'
import Button from '@toasttab/shared/src/components/atoms/Button/Button'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { headingListIcon } from '@toasttab/shared/src/utils/Contentful/styledList'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import MainFormContentful from '@toasttab/shared/src/components/molecules/Contentful/Form/MainFormContentful'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconClose } from '@toasttab/shared/src/assets/svg/icon-close.svg'

interface HeroModalProps {
  modal: any
  input: any
  buttonText: string
}

const HeroModal: React.FC<HeroModalProps> = ({ modal, input, buttonText }) => {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [extraInitValues, setExtraInitValues] = useState({})
  useEffect(() => {
    const emailCookie = _helpers.getCookie('email')
    if (emailCookie) {
      setEmail(emailCookie)
    }
  }, [])

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handleEmailValidation = () => {
    // Clear any existing errors
    setError('')

    if (_helpers.validateEmail(email)) {
      setError('Invalid email address')
      return false
    } else {
      _helpers.createCookie('email', email, 525600, '/')
      setExtraInitValues({
        email: email
      })
      // @ts-ignore - Typescript doesn't like setting new properties on window
      const heapHandler = window.heap
      if (typeof window !== 'undefined' && typeof heapHandler !== 'undefined') {
        heapHandler.addUserProperties({
          email: email.toLowerCase()
        })
        heapHandler.identify(email.toLowerCase())
      }
    }
    return true
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (handleEmailValidation()) {
      setShowModal(true)
    }
  }

  return (
    <>
      <div className='relative w-full'>
        <form onSubmit={handleFormSubmit}>
          <input
            type='email'
            name={input.fieldInputNameMarketoRestApi}
            id={input.fieldInputNameMarketoRestApi}
            placeholder={input.fieldLabel + '*'}
            className={`w-full h-16 border rounded-lg text-base text-secondary placeholder:text-secondary pl-4 pr-[166px] py-2 box-border ${
              error ? 'border-error' : 'border-[#C0C0C0]'
            }`}
            value={email}
            disabled={showModal}
            onChange={handleEmailChange}
            onBlur={handleEmailValidation}
            required={input.isRequired}
          />
          <div className='absolute right-[8px] top-2 bottom-2'>
            <Button
              type='submit'
              variation='primary'
              size='none'
              color='primary'
              fontWeight={['font-semibold']}
              classes='h-full px-8 text-white border-none cursor-pointer text-base max-w-[142px] min-w-0 flex-shrink-0 rounded-[20px]'
            >
              {buttonText}
            </Button>
          </div>
        </form>
        {error && (
          <p className='absolute top-full w-max flex flex-row font-sansPro items-center text-error text-xs font-semibold left-0 leading-4 xl:-mb-[6px] before:content-[""] before:bg-icon-warning-outline before:bg-center before:bg-contain before:bg-no-repeat before:w-4 before:h-4 before:block before:mr-2'>
            {error}
          </p>
        )}
      </div>
      {/* Modal */}
      {showModal && modal && (
        <div
          className='fixed inset-0 bg-black/30 flex items-center justify-center z-[101] top-0 left-0 right-0 bottom-0'
          onClick={() => setShowModal(false)}
        >
          <div
            className='rounded-lg max-w-[936px] min-h-[544px] w-full h-auto mx-4 my-4 flex overflow-hidden z-[102]'
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left half - #F6F1EE background */}
            <div className='flex-1 bg-[#F6F1EE] p-8'>
              <h3 className='mb-4'>{modal?.heading}</h3>
              {modal?.headingList && modal?.headingList?.length > 0 && (
                <ul className='list-none mb-10'>
                  {modal.headingList.map((item: any, i: number) => (
                    <li
                      key={i}
                      className={`flex text-base text-secondary mb-1 last:mb-none`}
                    >
                      <span
                        className={`mr-2 h-4 w-4 before:mr-1 before:h-4 before:w-4 before:inline-block ${headingListIcon(
                          item.key
                        )}`}
                      />
                      {item.value.content[0].content[0].value}
                    </li>
                  ))}
                </ul>
              )}
              {modal?.imageUrl && (
                <picture>
                  <source
                    srcSet={modal.imageUrl + '?fm=avif'}
                    type='image/avif'
                  />
                  <img
                    src={modal.imageUrl + '?fm=png'}
                    alt={modal.heading}
                    className='lazyload mb-4'
                  />
                </picture>
              )}
              {modal?.link && (
                <TextLink
                  classes='relative '
                  heading={modal.link.heading}
                  href={modal.link.url}
                  icon='arrow'
                  isDisplayedBlock
                  text={modal.link.text}
                  wrapperClasses='mt-none border-t border-solid border-black border-opacity-80 hidden lg:block [&>span]:mr-2'
                  target='_blank'
                />
              )}
            </div>

            {/* Right half - White background */}
            <div className='flex-1 bg-white px-8 pb-8 pt-6 [&>div>form>div>button]:!w-auto'>
              <div className='flex justify-end mb-6'>
                <IconClose
                  className='w-6 h-6 cursor-pointer'
                  fill='#666666'
                  onClick={() => setShowModal(false)}
                />
              </div>
              <MainFormContentful
                {...modal.form.data}
                componentContext='ContentfulMdsShowcaseForm'
                fieldIdPrefix='ContentfulHomepageHeroDemoRequest'
                useRecaptcha={false}
                initExtraValues={extraInitValues}
              />
              <p
                className='text-xs font-normal leading-4 font-sansPro text-secondary [&>a]:underline [&>a]:text-link [&>a]:inline-block mt-4'
                dangerouslySetInnerHTML={{
                  __html: modal.form.data.privacyStatement
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HeroModal
